import { toNumberOrUndefined } from '@daymade/common-lib/parsing';
import { ensureKey } from '@daymade/common-lib/types';
import { useEffect, useId, useMemo, useState } from 'react';
import { runningLocally } from 'utils/constants';

export interface TimeLeft {
  currentTimeMillisFromHydration: number | undefined;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const EMPTY_TIME: TimeLeft = {
  currentTimeMillisFromHydration: undefined,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

const getTimeLeft = (
  targetDate?: Date | null,
  currentTimeFromHydration?: Date,
): TimeLeft => {
  if (!targetDate) return EMPTY_TIME;

  const currentTimeMillis = currentTimeFromHydration?.getTime() || Date.now();
  const difference = targetDate.getTime() - currentTimeMillis;

  if (difference <= 0) return EMPTY_TIME;

  return {
    currentTimeMillisFromHydration: currentTimeFromHydration
      ? currentTimeMillis
      : undefined,
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

const HYDRATION_MILLIS_ATTRIBUTE_NAME = 'data-millis-for-hydration';
/** Provides the time left for our countdown around the site. Note that when this is rendered on the
 * server-side it will provide one set of values, and then by the time the hook is rendered on the
 * client-side the current time may have changed. This is made worse if the page is cached on the
 * server.
 *
 * Therefore, you should provide a 'currentTime' argument, which contains the time used on the
 * server so that the initial render matches. The useEffect will fire immediately after render
 * and show the updated time.
 *
 * The hook will return the currentTime used in the calculation so that it can be included in
 * any server-side rendered output. That output can then be read and passed into the hook
 * on the client-side
 */
export const useCountdown = (targetDate?: Date | null) => {
  const id = ensureKey({ useCountdown }, 'useCountdown') + useId();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => {
    // Read the time from the DOM if we're running client-side. We'll use this time so that our time matches the time when
    // the page was rendered on the server.

    let timeToUse: Date | undefined;
    if (runningLocally) {
      // When running in NextJS development mode (and probably caused by hot-reloading), the IDs generated on the server
      // side do not match the IDs on the client side, therefore we are unable to nab the time that the server used to render the component from the HTML. To prevent hydration errors in dev, we therefore have to not show
      // the time left until we have completed one render.
      //
      // If you wish to debug this and try to come up with a better solution, then comment out the return statement below:
      return getTimeLeft(null);
    }
    if (typeof window === 'undefined') {
      // When computing the initial state, and running on the server, always use the current time
      timeToUse = new Date();
    } else {
      // When computing the initial state, and running on the client, use the currentTimeMillisFromHydration value
      // that we have been given. Subsequent renders won't have the attribute so will use the current time
      const millis = toNumberOrUndefined(
        window.document
          .getElementById(id)
          ?.getAttribute(HYDRATION_MILLIS_ATTRIBUTE_NAME) ?? '',
      );
      timeToUse = millis ? new Date(millis) : undefined;
    }
    return getTimeLeft(targetDate, timeToUse);
  });

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft(EMPTY_TIME);
      return;
    }

    const update = () => {
      setTimeLeft(getTimeLeft(targetDate));
    };

    update();

    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const paddedTimeLeft = useMemo(
    () => ({
      days: timeLeft.days.toString().padStart(2, '0'),
      hours: timeLeft.hours.toString().padStart(2, '0'),
      minutes: timeLeft.minutes.toString().padStart(2, '0'),
      seconds: timeLeft.seconds.toString().padStart(2, '0'),
    }),
    [timeLeft],
  );

  return {
    timeLeft,
    paddedTimeLeft,
    isExpired:
      !targetDate ||
      (timeLeft.days === 0 &&
        timeLeft.hours === 0 &&
        timeLeft.minutes === 0 &&
        timeLeft.seconds === 0),
    domProps: {
      id: id,
      [HYDRATION_MILLIS_ATTRIBUTE_NAME]:
        timeLeft.currentTimeMillisFromHydration,
    },
  };
};
